import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useMemo, useRef, useEffect } from "react";
import "./accommodationReservationChart.scss";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { IGroupedUnitTypeReservations } from "pages/secure/facility/accommodation/calendar/AccommodationCalendar";
import { Badge } from "components/badge/Badge";
import classNames from "classnames";
import { faLock, faBookOpen, faCircleCheck, faClock } from "@fortawesome/pro-solid-svg-icons";
import { useHistory } from "react-router-dom";
import { IAccommodationReservation } from "pages/secure/facility/accommodation/reservations/types";

// export interface IReservationDropPayload {
//   reservationId: number;
//   targetUnitId: number;
//   targetDate: Date;
//   durationDays: number;
// }

interface IAccommodationReservationChartProps {
  startDate?: Date;
  endDate?: Date;
  daysToShow?: number;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  data: IGroupedUnitTypeReservations[];
  onClick?: (unitId: number, date: Date) => void;
}

// --- Helpers ---
const normalizeDate = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const parseDate = (str: string) => {
  if (!str) {
    return new Date();
  }
  const datePart = str.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const getDaysArray = (start: Date, end: Date) => {
  const arr = [];
  const dt = normalizeDate(start);
  const lastDay = normalizeDate(end);

  while (dt <= lastDay) {
    arr.push(new Date(dt));
    dt.setDate(dt.getDate() + 1);
  }
  return arr;
};

export default function AccommodationReservationChart({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  data,
  onClick,
}: IAccommodationReservationChartProps) {
  const [expandedUnitTypes, setExpandedUnitTypes] = useState<Record<number, boolean>>({});

  const history = useHistory();

  const headerScrollContainerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Config
  const cellWidth = 160;
  const rowHeight = 60;

  const normalizedStartDate = useMemo(() => {
    return startDate ? normalizeDate(startDate) : normalizeDate(new Date());
  }, [startDate]);

  const normalizedEndDate = useMemo(() => {
    return endDate ? normalizeDate(endDate) : normalizeDate(new Date());
  }, [endDate]);

  const dates = useMemo(
    () => getDaysArray(normalizedStartDate, normalizedEndDate),
    [normalizedStartDate, normalizedEndDate],
  );

  useEffect(() => {
    const initialExpanded: Record<number, boolean> = {};
    data.forEach(group => {
      //change to false if we want to start with all unit types closed
      initialExpanded[group.unitType.id] = true;
    });
    setExpandedUnitTypes(initialExpanded);
  }, [data]);

  const handleScroll = () => {
    if (bodyRef.current && headerScrollContainerRef.current) {
      // Set the header's scrollLeft to match the body's scrollLeft
      headerScrollContainerRef.current.scrollLeft = bodyRef.current.scrollLeft;
    }
  };

  // --- Drag and Drop Logic ---

  // const handleDragStart = (e: React.DragEvent, booking: Booking) => {
  //   // Calculate duration so we can preserve it on drop
  //   const start = parseDate(booking.start);
  //   const end = parseDate(booking.end);
  //   const durationMs = end.getTime() - start.getTime();
  //   const daysDuration = Math.round(durationMs / (1000 * 60 * 60 * 24));

  //   e.dataTransfer.setData("bookingId", booking.id);
  //   e.dataTransfer.setData("duration", daysDuration.toString());
  //   e.dataTransfer.effectAllowed = "move";
  // };

  // const handleDragStart = (e: React.DragEvent, reservation: IPropertyReservation) => {
  //   if (!reservation.units) {
  //     return;
  //   }

  //   const start = parseDate(reservation.arrival_date.toString());
  //   const end = parseDate(reservation.departure_date.toString());

  //   const durationMs = end.getTime() - start.getTime();
  //   const daysDuration = Math.round(durationMs / (1000 * 60 * 60 * 24));

  //   e.dataTransfer.setData("reservationId", reservation.id.toString());
  //   e.dataTransfer.setData("duration", daysDuration.toString());
  //   e.dataTransfer.effectAllowed = "move";
  // };

  // const handleDragOver = (e: React.DragEvent) => {
  //   e.preventDefault(); // Necessary to allow dropping
  //   e.dataTransfer.dropEffect = "move";
  // };

  // const handleDrop = (e: React.DragEvent, targetDate: Date, targetUnitId: number) => {
  //   e.preventDefault();

  //   const reservationId = e.dataTransfer.getData("reservationId");
  //   const durationStr = e.dataTransfer.getData("duration");
  //   const durationDays = parseInt(durationStr, 10);

  //   if (reservationId && !isNaN(durationDays) && onReservationDrop) {
  //     onReservationDrop({
  //       reservationId: parseInt(reservationId, 10),
  //       targetUnitId,
  //       targetDate,
  //       durationDays,
  //     });
  //   }
  // };

  // const handleDrop = (e: React.DragEvent, targetDate: Date, targetResourceId: string) => {
  //   e.preventDefault();

  //   const bookingId = e.dataTransfer.getData("bookingId");
  //   const durationStr = e.dataTransfer.getData("duration");
  //   const durationDays = parseInt(durationStr, 10);

  //   if (bookingId && !isNaN(durationDays)) {
  //     const newStart = new Date(targetDate);
  //     const newEnd = new Date(targetDate);
  //     newEnd.setDate(newEnd.getDate() + durationDays);

  //     setBookings(prev =>
  //       prev.map(b => {
  //         if (b.id === bookingId) {
  //           return {
  //             ...b,
  //             resourceId: targetResourceId,
  //             start: formatDate(newStart),
  //             end: formatDate(newEnd),
  //           };
  //         }
  //         return b;
  //       }),
  //     );
  //   }
  // };

  // const getBookingStyle = (reservation: IPropertyReservation) => {
  //   const bookingStart = parseDate(reservation.arrival_date.toString());
  //   const bookingEnd = parseDate(reservation.departure_date.toString());
  //   const durationMs = bookingEnd.getTime() - bookingStart.getTime();
  //   const nights = Math.round(durationMs / (1000 * 60 * 60 * 24));

  //   const offsetMs = bookingStart.getTime() - startDate.getTime();
  //   const offsetDays = Math.round(offsetMs / (1000 * 60 * 60 * 24));

  //   const halfCell = cellWidth / 2;

  //   return {
  //     left: `${offsetDays * cellWidth + halfCell}px`,
  //     width: `${nights * cellWidth}px`,
  //     height: `${rowHeight - 12}px`,
  //   };
  // };

  const getBookingStyle = (reservation: IAccommodationReservation) => {
    const startStr =
      reservation.arrival_date instanceof Date
        ? reservation.arrival_date.toISOString()
        : String(reservation.arrival_date);

    const endStr =
      reservation.departure_date instanceof Date
        ? reservation.departure_date.toISOString()
        : String(reservation.departure_date);

    const bookingStart = parseDate(startStr);
    const bookingEnd = parseDate(endStr);
    const durationMs = bookingEnd.getTime() - bookingStart.getTime();
    const nights = Math.round(durationMs / (1000 * 60 * 60 * 24));
    const offsetMs = bookingStart.getTime() - normalizedStartDate.getTime();
    const offsetDays = Math.round(offsetMs / (1000 * 60 * 60 * 24));

    const halfCell = cellWidth / 2;

    return {
      left: `${offsetDays * cellWidth + halfCell}px`,
      width: `${nights * cellWidth}px`,
      height: `${rowHeight - 12}px`,
    };
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  function setDateRangeStart(date: Date) {
    onStartDateChange(date);
  }

  function setDateRangeEnd(date: Date) {
    onEndDateChange(date);
  }

  return (
    <>
      <div className="tc-container">
        <div className="tc-viewport">
          <div className="tc-header-row">
            <div className="tc-corner-cell">
              <div className="tc-controls">
                <DatePickerInput
                  months={1}
                  position="left"
                  showQuickOptions
                  startingDate={startDate}
                  setStartingDate={setDateRangeStart}
                  endingDate={endDate}
                  setEndingDate={setDateRangeEnd}
                  shorterDateString
                />
              </div>
            </div>

            <div className="tc-header-scroll-container" ref={headerScrollContainerRef}>
              <div className="tc-header-track">
                {dates.map((date, i) => {
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const today = isToday(date);
                  return (
                    <div
                      key={i}
                      className={`tc-header-cell ${isWeekend ? "tc-bg-weekend" : ""}`}
                      style={{ borderRight: "none" }}
                    >
                      <span className={`tc-header-date ${today ? "is-today" : ""}`}>{date.getDate()}</span>
                      <span className="tc-header-day">{date.toLocaleDateString(undefined, { weekday: "short" })}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="tc-body" ref={bodyRef} onScroll={handleScroll}>
            <div className="tc-grid-content">
              {data?.map(group => {
                const isExpanded = expandedUnitTypes[group.unitType.id] ?? true;
                return (
                  <React.Fragment key={group.unitType.id}>
                    {/* Top row for unit type */}
                    <div className="tc-row tc-unit-type-row">
                      <div
                        className="tc-sidebar-cell"
                        onClick={() => {
                          setExpandedUnitTypes(prev => ({
                            ...prev,
                            [group.unitType.id]: !prev[group.unitType.id],
                          }));
                        }}
                      >
                        <div className="flex gap-4 items-center">
                          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronUp} />
                          <p className="tc-unit-type-name">{group.unitType.title}</p>
                          {(() => {
                            const groupUnitIds = new Set(group.units.map(u => u.id));
                            const displayedReservationsCount = group.reservations.filter(res =>
                              res.units?.some(u => groupUnitIds.has(u.id)),
                            ).length;

                            return (
                              displayedReservationsCount > 0 && (
                                <p>
                                  <Badge type="primary">{displayedReservationsCount}</Badge>
                                </p>
                              )
                            );
                          })()}
                        </div>
                      </div>

                      <div className="tc-grid-track">
                        {dates.map((date, i) => {
                          const dateStr = date.toISOString().split("T")[0];
                          const price = group?.pricesByDate?.[dateStr];

                          return (
                            <div
                              key={i}
                              className="tc-grid-cell"
                              style={{ borderRight: "none", backgroundColor: "white" }}
                            >
                              <p className="tc-header-day">{price !== undefined ? `$${price}` : ""}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {isExpanded &&
                      group.units.map(unit => {
                        return (
                          <div key={unit.id} className="tc-row">
                            {/* Sidebar */}
                            <div className="tc-sidebar-cell" style={{ cursor: "default" }}>
                              <span className="tc-resource-name">{unit.title}</span>
                              <span className="tc-resource-type"></span>
                            </div>

                            {/* Grid Background */}
                            <div className="tc-grid-track">
                              {(() => {
                                const unitReservations = group.reservations.filter(reservation =>
                                  reservation.units?.some(u => u.id === unit.id),
                                );

                                return dates.map((date, i) => {
                                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                                  const isOccupied = unitReservations.some(res => {
                                    const arrival = normalizeDate(parseDate(String(res.arrival_date)));
                                    const departure = normalizeDate(parseDate(String(res.departure_date)));
                                    const cellDate = normalizeDate(date);
                                    return cellDate >= arrival && cellDate < departure;
                                  });

                                  const isClickable = !isOccupied && !!onClick;

                                  return (
                                    <div
                                      key={i}
                                      className={classNames("tc-grid-cell", {
                                        weekend: isWeekend,
                                        "is-available": isClickable,
                                      })}
                                      onClick={isClickable ? () => onClick(unit.id, date) : undefined}
                                      style={{ cursor: isClickable ? "pointer" : "default" }}
                                    />
                                  );
                                });
                              })()}

                              {/* Bookings */}
                              {group.reservations
                                .filter(reservation => reservation.units?.some(u => u.id === unit.id))
                                .map(reservation => {
                                  const style = getBookingStyle(reservation);

                                  return (
                                    <div
                                      key={reservation.id}
                                      // className={`tc-booking status-confirmed`}
                                      className={classNames("tc-booking status-confirmed", {
                                        "status-locked": reservation?.status === "locked",
                                        "status-checked-in": reservation?.status === "checked-in",
                                        "status-checked-out": reservation?.status === "checked-out",
                                        "status-pending": reservation?.status === "pending",
                                      })}
                                      style={style}
                                      title={`${reservation.customer.full_name}`}
                                      // draggable={true}
                                      // onDragStart={e => handleDragStart(e, reservation)}
                                      onClick={
                                        reservation?.status === "locked"
                                          ? () => {}
                                          : () =>
                                              history.push(
                                                "/admin/accommodation/reservations/" + String(reservation.id),
                                              )
                                      }
                                    >
                                      {reservation?.status === "locked" ? (
                                        <div className="flex justify-center w-full">
                                          <FontAwesomeIcon
                                            className="status-locked-icon"
                                            icon={faLock}
                                            style={{
                                              width: "14px",
                                              height: "14px",
                                              flexShrink: 0,
                                            }}
                                          />
                                        </div>
                                      ) : (
                                        <>
                                          <FontAwesomeIcon
                                            icon={
                                              reservation?.status === "checked-in"
                                                ? faCircleCheck
                                                : reservation?.status === "checked-out"
                                                ? faClock
                                                : faBookOpen
                                            }
                                            style={{
                                              width: "12px",
                                              height: "12px",
                                              marginRight: "10px",
                                              flexShrink: 0,
                                              opacity: 0.8,
                                            }}
                                          />
                                          <span className="tc-booking-name">{reservation.customer.full_name}</span>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="tc-legend">
          {/* <div className="tc-legend-item">
            <div className="tc-dot tc-dot-confirmed"></div> Confirmed
          </div> */}
          <div className="tc-legend-item">
            <div className="tc-dot tc-dot-locked">
              <FontAwesomeIcon icon={faLock} className="tc-dot-locked-icon" />
            </div>{" "}
            Locked
          </div>
          <div className="tc-legend-item">
            <div className="tc-dot tc-dot-checked-in"></div> Checked In
          </div>
          <div className="tc-legend-item">
            <div className="tc-dot tc-dot-checked-out"></div> Checked Out
          </div>
          <div className="tc-legend-item">
            <div className="tc-dot tc-dot-pending"></div> Pending
          </div>
        </div>
      </div>
    </>
  );
}
